import { ArpMedia, ArpMediaType } from '@arpeggi/shared'
import { IconButton, Icons, Tag } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC } from 'react'
import { Link } from 'react-router-dom'
import {
  DotsVertical,
  HeartFilled,
  HeartOutlined,
  PauseIcon,
  PlayIcon,
  StemIcon
} from '../../__legacy__/components/core/icons'
import { Soundwave } from '../../__legacy__/components/core/icons/responsive-icons'
import { DetailsButton } from '../../__legacy__/components/platform/new-explore/components/TableRow'
import { formatTime } from '../../__legacy__/components/player/PlayerUtils'
import { useRemixArpMedia } from '../arp-utils'
import { TableRowContext, useDateMinted, useFavoriteButton, useMedia, useTags } from './hooks'
import { Column, MainColumn, Row } from './primitives'

type TableRowProps = ComponentProps<typeof Row> & {
  media: ArpMedia
  active?: boolean
}

const Root: FC<TableRowProps> = ({ media, children, ...props }) => {
  return (
    <TableRowContext.Provider value={media}>
      <Row {...props}>{children}</Row>
    </TableRowContext.Provider>
  )
}

const Main: FC = () => {
  const { user, metadata, type, id } = useMedia()
  const detailPageUrl = `/${type}?token=${id}`
  const hasIcon = type !== ArpMediaType.SONG
  const icon = type === ArpMediaType.STEM ? StemIcon : Soundwave
  return (
    <MainColumn.Root>
      <MainColumn.Avatar src={user.profilePicture?.url}>
        <MainColumn.Overlay id="avatar-overlay">
          <PlayIcon id="play-icon" />
          <PauseIcon id="pause-icon" />
        </MainColumn.Overlay>
      </MainColumn.Avatar>
      {hasIcon && <MainColumn.Icon as={icon} />}
      <MainColumn.Content>
        <MainColumn.Title id="title">
          <Link to={detailPageUrl} onClick={(e) => e.stopPropagation()}>
            {metadata.title}
          </Link>
        </MainColumn.Title>
        <MainColumn.ArtistName>
          <Link to={`/profile/${user.walletAddress}`} onClick={(e) => e.stopPropagation()}>
            {user.displayName}
          </Link>
        </MainColumn.ArtistName>
      </MainColumn.Content>
    </MainColumn.Root>
  )
}

const Tags: FC = () => {
  const tags = useTags()
  return (
    <Column.Root wrap layout={'fluid2'} hideOnMobile>
      {tags.map((tag) => (
        <Tag key={tag}>{tag}</Tag>
      ))}
    </Column.Root>
  )
}

const DateMinted: FC = () => {
  const date = useDateMinted()
  return (
    <Column.Root layout={'fluid'} hideOnMobile>
      <Column.Text>{date}</Column.Text>
    </Column.Root>
  )
}

const Favorite: FC = observer(() => {
  const { isFavorited, favoriteCount, handleClick } = useFavoriteButton()
  const icon = isFavorited ? <HeartFilled style={{ color: '#E8178A' }} /> : <HeartOutlined />
  return (
    <Column.Root layout={'fluid'} hideOnMobile onClick={(e) => e.stopPropagation()}>
      <IconButton size="sm" variant={'ghost'} onClick={handleClick} icon={icon} />
      <Column.Text>{favoriteCount}</Column.Text>
    </Column.Root>
  )
})

const Remixes: FC = () => {
  const { remixCount } = useMedia()
  return (
    <Column.Root layout={'fluid'} hideOnMobile>
      <Column.Text>{remixCount || 0}</Column.Text>
    </Column.Root>
  )
}

const Plays: FC = () => {
  const { playCount } = useMedia()
  return (
    <Column.Root layout={'fluid'} hideOnMobile>
      <Column.Text>{playCount || 0}</Column.Text>
    </Column.Root>
  )
}

const Duration: FC = () => {
  const { durationMs } = useMedia()
  const formattedDuration = formatTime(durationMs / 1000)
  return (
    <Column.Root layout={'fluid'} hideOnMobile>
      <Column.Text>{formattedDuration}</Column.Text>
    </Column.Root>
  )
}

const StudioButton: FC = observer(() => {
  const media = useMedia()
  const { remix, status } = useRemixArpMedia(media)
  return (
    <IconButton
      variant="secondary"
      icon={<Icons.Studio />}
      loading={status !== 'ready'}
      onClick={remix}
      css={{ display: 'none', '@tablet': { display: 'flex' } }}
    />
  )
})

const MoreIcon: FC = () => {
  const media = useMedia()
  return (
    <DetailsButton media={media}>
      <IconButton variant="ghost" icon={<DotsVertical />} />
    </DetailsButton>
  )
}

const Actions: FC = () => {
  return (
    <Column.Root layout={'noshrink'} onClick={(evt) => evt.stopPropagation()}>
      <StudioButton />
      <MoreIcon />
    </Column.Root>
  )
}

/**
 * Composable TableRow. Simple to mix and match different columns
 * @example
 * Full example. Note that you don't have to provide `IArpMedia` to the children elements
 * ```
 * <TableRow.Root media={media}>
 *   <TableRow.Main />
 *   <TableRow.Tags />
 *   <TableRow.DateMinted />
 *   <TableRow.Duration />
 *   <TableRow.Favorite />
 *   <TableRow.Remixes />
 *   <TableRow.Plays />
 *   <TableRow.Actions />
 * </TableRow.Root>
 * ```
 */
export const TableRow = {
  Root,
  Main,
  Tags,
  DateMinted,
  Favorite,
  Remixes,
  Plays,
  Actions,
  Duration
}
